import { Slide, Stack, Theme, Typography } from '@mui/material';
import { noop } from 'lodash';
import { FC } from 'react';
import { makeStyles } from 'tss-react/mui';
import { useSnapshot } from 'valtio';
import BottomDrawer from '../../elements/BottomDrawer/BottomDrawer';
import { closeArtifactModal } from '../actions/artifacts-actions';
import ArtifactCard from '../components/Drawer/ArtifactCard';
import ArtifactsStore from '../stores/artifacts-store';
import { getArtifactIconType, sumSeverities } from '../utils/artifacts-utils';
import { MdCampaign as Campaign } from 'react-icons/md';
import pluralize from 'pluralize';

const ArtifactDrawerContainer: FC<
  React.PropsWithChildren<ArtifactDrawerContainerProps>
> = ({ className }) => {
  const { classes, cx } = useStyles();

  const { selectedArtifact, loadingArtifact } = useSnapshot(ArtifactsStore);
  if (!selectedArtifact) {
    return null;
  }

  const totalSeverities = sumSeverities(
    Object.values(selectedArtifact.issuesBySeverity),
  );

  const ArtifactIcon = getArtifactIconType(selectedArtifact.artifactInfo.type);
  return (
    <BottomDrawer
      className={className}
      maxHeight='80%'
      title={
        <Stack direction='row' justifyContent='space-between'>
          <div className={classes.title}>
            {ArtifactIcon && <ArtifactIcon className={classes.icon} />}
            <Typography variant='body1' color='text.primary'>
              {selectedArtifact?.artifactInfo.name}, Version:
              {selectedArtifact?.artifactInfo.version
                ? selectedArtifact.artifactInfo.version
                : 'N/A'}
            </Typography>
          </div>
          <div className={classes.buttons}>
            {totalSeverities > 0 && (
              <Typography className={cx(classes.appsText, classes.appCount)}>
                <Campaign />
                {pluralize('Issue', totalSeverities, true)}
              </Typography>
            )}
          </div>
        </Stack>
      }
      onClose={closeArtifactModal}
      loading={loadingArtifact}
      defaultHeight={300}
      onResizeStop={noop}>
      <Slide direction='up' in>
        <div>
          <ArtifactCard artifact={selectedArtifact} />
        </div>
      </Slide>
    </BottomDrawer>
  );
};

const useStyles = makeStyles()((theme: Theme) => ({
  title: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  buttons: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  appCount: {
    color: '#5059FF',
    textAlign: 'center',
    padding: theme.spacing(0.5, 1.5),
    background:
      theme.palette.mode === 'dark'
        ? '#1C1F2A'
        : theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  appsText: {
    color: `${theme.palette.dashInventory.textColor.colorAppsText} !important`,
    whiteSpace: 'nowrap',
    fontSize: 14,
    minWidth: '5rem',
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing(1),
  },
  icon: {
    height: '1em',
  },
}));

export interface ArtifactDrawerContainerProps {
  className: string;
}

export default ArtifactDrawerContainer;
